import type {
  DiscountSummaryDto,
  LicensingModelDto,
  PaymentMethodDto,
} from '@gm-commercial/entitlements-client/src/api/entitlementsApi';
import type { ReactNode } from 'react';
import { BadgeStatus } from '~/common/components/badge/Badge';
import type { PaymentSummary } from '~/common/models/billing.model';
import { TAB_QUERY_PARAM } from '~/common/utils/tabSelection.utils';
import type { OrderDiscountDisplay } from '~/features/discounts/utils/discount.utils';

export type PlanLabel = {
  code: string;
  name: string;
};

export const PLAN_DETAILS_TAB_QUERY_PARAM = TAB_QUERY_PARAM;

export enum PlanManagementTab {
  ACTIVE = 'active',
  BILLING = 'billing',
  INVOICES = 'invoices',
  DISCOUNTS = 'discounts',
  DEVICE_ORDERS = 'device-orders',
  ONSTAR_PLANS = 'onstar-plans',
}

export enum PlanDetailsTab {
  OVERVIEW = 'overview',
  SUBSCRIPTION = 'subscription',
  PREPAID = 'prepaid',
  TRIAL = 'trial',
}

export type OverviewTileId = 'subscriptions' | 'prepaid' | 'trial' | 'eligible';

export enum OverviewTileValues {
  SUBSCRIPTIONS = 'subscriptions',
  PREPAID = 'prepaid',
  ON_TRIAL = 'trial',
  ELIGIBLE = 'eligible',
}

/** Effective price with optional list/compare-at for strikethrough display. */
export type DisplayPrice = {
  /** Payable / effective amount (discounted price when a discount applies). */
  amount: number;
  /** Pre-discount list amount when a discount applies. */
  compareAt?: number;
};

export interface SubscriptionDetails {
  productCode: string;
  productName?: string;
  counts?: { [key: string]: number };
  /**
   * Vehicles already on this plan: subscription-enrolled plus prepaid.
   * Cancel-plan uses this as the vehicle count when the whole plan is cancelled.
   */
  totalEnrolledVehicles: number;
  nextBcdDate: string;
  expirationDate?: string;
  paymentMethod?: PaymentMethodDto;
  isScheduledForCancellation: boolean;
  /**
   * Plan unit price. `amount` is payable (client-calculated discounted unit when
   * a discount applies); `compareAt` is catalog list. Undefined when the
   * licensing model has no unit price.
   */
  costPerVehicle?: DisplayPrice;
  /** Price per vehicle for older model years (undefined if not available) */
  pricePerVehicleOlderModels?: DisplayPrice;
  /** Boundary year for older models (e.g., 2024 means "2024 and earlier") */
  olderModelsYear?: number;
  /** Price per vehicle for newer model years (undefined if not available) */
  pricePerVehicleNewerModels?: DisplayPrice;
  /** Boundary year for newer models (e.g., 2025 means "2025 and newer") */
  newerModelsYear?: number;
  /**
   * Next payment from recurring invoice: `amount` = `total` (post-tax);
   * `compareAt` = `subTotal` (pre-tax) when `discountAmount != null`.
   * Tax-basis mismatch is known; TODO(SDPCPLT-8220) to align.
   */
  nextPayment: DisplayPrice & { date?: string };
  /**
   * Whether the Next Payment row should show amount/date instead of an empty
   * placeholder. False when scheduled for cancellation, or when there is no
   * upcoming payment amount or date.
   */
  hasNextPayment: boolean;
  lastPaymentAmount: number;
  lastPaymentDate?: string;
  billingCycleDuration: BillingCycleDuration;
  /** Zuora subscription number for the licensed subscription. Undefined for trial licenses and licenses with no Zuora subscription. Required by the apply post-sales discount endpoint. */
  subscriptionNumber?: string;
  hasActiveDiscount: boolean;
  /** Discount id for post-sales change/cancel. Undefined when no discount exists. */
  discountId?: string;
  /**
   * Raw discount summary for PlanDiscountModal prefill. Undefined when the
   * subscription has no discount payload.
   */
  discount?: DiscountSummaryDto;
  /** Allowed percents for the active licensing model. Empty when not applicable. */
  allowedDiscountPercents: number[];
  /** ABP model-year tooltip payload when attribute-based pricing is enabled and both monthly/yearly tiers exist. */
  pricingTooltip?: PricingTooltipData;
}

export enum ProductCodes {
  OSS = 'OSS', // OnStar Protect Plan
  OST = 'OST', // OnStar Telematics Plan
  OSCP = 'OSCP', // OnStar Connect Plus Plan
  OSOF = 'OSOF', // OnStar One Fleet Plan
  OVT = 'OVT', // OnStar Video Telematics
  DUALCAM = 'DUALCAM', // Dual Camera Hardware Product
  GTADAPTER = 'GTADAPTER', // Fleet Adapter Hardware Product
  AHARN = 'AHARN', // Harness Hardware Product
  OSVA_MX = 'OSVA_MX', // Stolen Vehicle Assistance (Mexico)
  OSW_MX = 'OSW_MX', // OnStar WiFi (Mexico)
  OSS_MX = 'OSS_MX', // OnStar Protect Plan (Mexico)
  OST_MX = 'OST_MX', // OnStar Telematics Plan (Mexico)
}

export enum ProductEligibilityType {
  SERVICE = 'SERVICE',
  HARDWARE = 'HARDWARE',
}

/**
 * Asset type values used as a query parameter in subscription/eligibility API calls.
 * Matches the API contract: `'vehicle'` or `'peripheral'`.
 */
export const EligibilityAssetType = {
  VEHICLE: 'vehicle',
  PERIPHERAL: 'peripheral',
} as const;

export type EligibilityAssetType =
  (typeof EligibilityAssetType)[keyof typeof EligibilityAssetType];

/**
 * Feature codes paired with {@link ProductCodes} in LaunchDarkly plan / marketplace
 * enablement and VIN enablement checks. Add entries incrementally; do not mirror the full backend catalog.
 */
export enum FeatureCodes {
  TELE = 'TELE',
  RVDE = 'RVDE',
  MAPS = 'MAPS',
  HOSP = 'HOSP',
  AUDI = 'AUDI',
}

/**
 * Internal product metadata structure used in PRODUCTS configuration
 */
export interface ProductMetadataInternal {
  productCode: ProductCodes;
  name: string;
  badgeVariant: BadgeStatus;
}

/**
 * Product metadata interface containing all UI-related attributes
 * for a given product/plan
 */
export interface ProductMetadata extends ProductMetadataInternal {
  icon: ReactNode;
}

/**
 * Centralized product configuration mapping
 * Contains all UI-related metadata for each product

 */
export const PRODUCTS: Record<ProductCodes, ProductMetadataInternal> = {
  [ProductCodes.OSS]: {
    productCode: ProductCodes.OSS,
    name: 'entitlements:plans.protect',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.OST]: {
    productCode: ProductCodes.OST,
    name: 'entitlements:plans.telematics',
    badgeVariant: BadgeStatus.WARNING,
  },
  [ProductCodes.OSCP]: {
    productCode: ProductCodes.OSCP,
    name: 'entitlements:plans.connectPlus',
    badgeVariant: BadgeStatus.TEAL,
  },
  [ProductCodes.OSOF]: {
    productCode: ProductCodes.OSOF,
    name: 'entitlements:plans.oneFleet',
    badgeVariant: BadgeStatus.PURPLE_GRADIENT,
  },
  [ProductCodes.OVT]: {
    productCode: ProductCodes.OVT,
    name: 'entitlements:plans.videoTelematics',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.DUALCAM]: {
    productCode: ProductCodes.DUALCAM,
    name: 'entitlements:addOns.dualCamera',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.GTADAPTER]: {
    productCode: ProductCodes.GTADAPTER,
    name: 'entitlements:addOns.fleetAdapter',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.AHARN]: {
    productCode: ProductCodes.AHARN,
    name: 'entitlements:addOns.harness',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.OSVA_MX]: {
    productCode: ProductCodes.OSVA_MX,
    name: 'entitlements:plans.stolenVehicleAssistance',
    badgeVariant: BadgeStatus.TEAL,
  },
  [ProductCodes.OSW_MX]: {
    productCode: ProductCodes.OSW_MX,
    name: 'entitlements:plans.wifi',
    badgeVariant: BadgeStatus.WHITE,
  },
  [ProductCodes.OSS_MX]: {
    productCode: ProductCodes.OSS_MX,
    name: 'entitlements:plans.protect',
    badgeVariant: BadgeStatus.INFO,
  },
  [ProductCodes.OST_MX]: {
    productCode: ProductCodes.OST_MX,
    name: 'entitlements:plans.telematics',
    badgeVariant: BadgeStatus.WARNING,
  },
} as const;

export const ONE_FLEET_PLANS: ProductCodes[] = [
  ProductCodes.OST,
  ProductCodes.OSS,
  ProductCodes.OSCP,
];

/**
 * Products whose Plan & Billing surfaces are device-centric (counted, priced, and
 * enrolled per device rather than per vehicle), e.g. Video Telematics cameras.
 * Gated behind SHOP_ADD_ONS_FLAG. Drives "/device" pricing copy, "Enroll devices"
 * CTAs, and the device-first table column sets.
 */
export const DEVICE_BASED_PRODUCT_CODES: ProductCodes[] = [ProductCodes.OVT];

export const VEHICLE_DEVICE_BASED_PRODUCT_CODES: ProductCodes[] = [
  ProductCodes.OST,
];

/**
 * Row asset variants rendered in Plan & Billing tables. VEHICLE is the default
 * vehicle identity row; CAMERA (Video Telematics cameras) and ADAPTER (fleet
 * adapters) render the device identity cell instead.
 */
export const PlanAssetType = {
  VEHICLE: 'VEHICLE',
  CAMERA: 'CAMERA',
  ADAPTER: 'ADAPTER',
} as const;

export type PlanAssetType = (typeof PlanAssetType)[keyof typeof PlanAssetType];

export interface ActivePlanPartial {
  paymentSummary?: PaymentSummary;
  planSummary: PlanSummary;
}

export interface PlanSummary {
  productCode: string;
  pricePerVehicle: number;
  billingCycleDuration: BillingCycleDuration;
  nextBcdDate: string;
  licensingModelId: string;
  licensingModels?: LicensingModelDto[];
}

export const PlanStateType = {
  RPO: 'RPO',
  ELIGIBLE: 'ELIGIBLE',
  ENROLLED: 'ENROLLED',
  TRIAL: 'TRIAL',
  ASSIGNABLE: 'ASSIGNABLE',
  REDEEMABLE: 'REDEEMABLE',
} as const;

export const VinTypes = {
  NON_TRIAL_ONLY: 'NON_TRIAL_ONLY',
  TRIAL_ONLY: 'TRIAL_ONLY',
  TRIAL_AND_NON_TRIAL: 'TRIAL_AND_NON_TRIAL',
} as const;

export type VinType = (typeof VinTypes)[keyof typeof VinTypes];

/**
 * License types from the entitlements API - ProductLicenseDto type
 */
export const ProductLicenseType = {
  SUBSCRIPTION: 'SUBSCRIPTION',
  RPO: 'RPO',
  MODEL_YEAR: 'MODEL_YEAR',
  MAKE: 'MAKE',
  MODEL: 'MODEL',
  TRIAL: 'TRIAL',
} as const;

/**
 * Billing cycle duration represent keys which can be used to map license models.
 * Currently individual license models do not contain unique identifiers.
 */
export enum BillingCycleDuration {
  MONTHLY = 'P1M',
  YEARLY = 'P12M',
}

export const BILLING_CYCLE_LABELS: Record<BillingCycleDuration, string> = {
  [BillingCycleDuration.MONTHLY]: 'common:billing.monthly',
  [BillingCycleDuration.YEARLY]: 'common:billing.yearly',
} as const;

export const BILLING_CYCLE_ABBREVIATIONS: Record<BillingCycleDuration, string> =
  {
    [BillingCycleDuration.MONTHLY]: 'common:billing.monthlyAbbreviated',
    [BillingCycleDuration.YEARLY]: 'common:billing.yearlyAbbreviated',
  } as const;

export const YEAR_LABEL_KEYS = {
  newer: 'catalog:order.myYearAndNewer',
  older: 'catalog:order.myYearAndEarlier',
} as const;

export enum PlanBadgeState {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  ENDING = 'ENDING',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

export interface ActivePlan {
  id: string;
  productCode: ProductCodes;
  vehicleSubscriptions: number;
  eligibleVehicles: number;
  assignableVehicles: number;
  prePaidVehicles: number;
  trialVehicles: number;
  isSubscriptionActive: boolean;
  billingCycleDuration: BillingCycleDuration | null;
  isScheduledForCancellation: boolean; // true if plan will be cancelled at end of billing cycle
  nextBcdDate: string; // ISO date string indicating the next billing cycle date
  trialExpirationDate: string | null;
  /** Allowed discount percentages for this plan's active licensing model. Empty if discounts are not applicable. */
  allowedDiscountPercents: number[];
  discount?: DiscountSummaryDto;
  expirationDate?: string; // ISO date string indicating the subscription expiration date
  /** Zuora subscription number for the licensed subscription. Undefined for trial licenses and licenses with no Zuora subscription. Required by the apply post-sales discount endpoint. */
  subscriptionNumber?: string;
}

export interface VehicleCountBarChartDetails {
  subscriptionCount: number;
  prepaidCount: number;
  trialCount: number;
  eligibleCount: number;
  totalVehicleCount: number;
}

/**
 * Represents a billing cycle option with all necessary data for both rendering and API calls.
 */
export interface BillingOption {
  /** ISO 8601 duration string ('P1M' or 'P12M') */
  duration: BillingCycleDuration;
  /**
   * Licensing model id for API calls. Absent only for add-on options from
   * `billingOptionsFromCompanionPricing`, which never key an API call.
   */
  licensingModelId?: string;
  /** Price per vehicle for this billing cycle */
  price: number;
  /** Translation key for full label (e.g., 'Monthly') */
  label: string;
  /** Translation key for abbreviated label (e.g., 'mo') */
  abbreviatedLabel: string;
  /** Whether this option is disabled */
  isDisabled: boolean;
  /** Tooltip text shown when disabled */
  disabledTooltip?: string;
  /** Savings percentage for yearly vs monthly (e.g., 33 for 33% savings) */
  savingsPercentage?: number;
}

/**
 * Attribute-based pricing types (model-year pricing tiers, etc.)
 */

/**
 * Model-year pricing when all four fields are present and valid.
 * Returned by getPricesPerVehicleByModelYear when data is complete; otherwise the function returns undefined.
 */
export type ModelYearPricing = {
  olderModelsPrice: number;
  /** Boundary year for older models, as a string for i18n interpolation (e.g. "2024") */
  olderModelsYear: string;
  newerModelsPrice: number;
  /** Boundary year for newer models, as a string for i18n interpolation (e.g. "2025") */
  newerModelsYear: string;
};

/**
 * A pricing group derived from a charge item in the preview API response,
 * enriched with the model-year label from the product's pricingModels.
 */
export type PricingGroup = {
  label: 'newer' | 'older';
  year: number;
  unitPrice: number;
  quantity: number;
  subTotal: number;
};

/**
 * Pre-formatted data for the pricing tooltip when a plan has model-year-based pricing.
 * All values are formatted for display (years as strings, prices as currency strings).
 */
export type PricingTooltipData = {
  newerModelYear: string;
  olderModelYear: string;
  newerMonthlyPrice: string;
  newerYearlyPrice: string;
  olderMonthlyPrice: string;
  olderYearlyPrice: string;
};

export type TrialVehiclesSummary = {
  count: number;
  earliestTrialEndDate: string;
};

export type OrderSummary = {
  planName: string;
  billingCycleDuration: BillingCycleDuration;
  vehicleCount: number;
  isProrated: boolean;
  pricePerVehicle: number;
  pricingGroups?: PricingGroup[];
  /**
   * Catalog-level model-year pricing for the plan, used to drive the dual-price
   * ABP renewal disclaimer. Sourced from the plan's licensingModels (not the
   * current order's charge items) so the disclaimer always reflects the plan's
   * renewal structure regardless of which tiers are in the current order.
   * Undefined for single-tier ABP plans (e.g. Telematics) and when ABP is off.
   */
  modelYearPricing?: ModelYearPricing;
  subtotalPrice: number;
  taxAmount: number;
  totalPrice: number;
  invoiceStartDate: string;
  invoiceEndDate: string;
  nextInvoiceStartDate: string;
  nextInvoiceSubtotalPrice: number;
  vinTypes?: VinType;
  isNewSubscription: boolean;
  discount?: OrderDiscountDisplay;
  reenrollmentCount?: number;
  trialVehiclesSummary?: TrialVehiclesSummary;
  allowedDiscountPercents?: number[];
  hasExistingSubscription?: boolean;
  /**
   * i18n key for the per-unit price row label. Defaults to
   * `catalog:order.pricePerVehicle`; the OST enroll preview overrides it to
   * reflect the selection ("per device" for adapters only, "per eligible" for a
   * mixed vehicles + adapters selection).
   */
  unitPriceLabelKey?: string;
  /**
   * i18n key that, when set, replaces the renewal disclaimer with a specific
   * override (takes precedence over the ABP / standard notices). Rendered with
   * `{ billingCycle, price }`. Set only by the Shop Add-Ons OST enroll preview
   * when the selection includes adapters, naming the devices and, for a mixed
   * selection, the vehicles alongside them; undefined everywhere else keeps
   * today's wording.
   */
  renewalNoticeOverrideKey?: string;
};

/**
 * Products whose enrollment and billing experience spans both vehicle plans and
 * peripheral devices.
 *
 * Used by Plan & Billing flows that need to treat mixed vehicle/peripheral
 * products as a shared category.
 */
export const VEHICLE_PERIPHERALS_BASED_PRODUCT_CODES: ProductCodes[] = [
  ProductCodes.OVT,
  ProductCodes.OST,
];
